import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AnnouncementsCenter } from "@/features/admin/components/announcements-center";
import { useChatSocket } from "@/features/chat/hooks/use-chat-socket";
import { ImpersonationBanner } from "@/features/ops/components/impersonation-banner";
import { AppTopBar } from "@/layouts/app-header";
import { AppSidebar } from "@/layouts/app-sidebar";
import { useUIStore } from "@/store/ui-store";
import { cn } from "@/lib/utils";

/**
 * Authenticated app shell (Calm Studio): a navy sidebar + a content column whose
 * first row is the search/bell/account bar (no fixed header band). A soft
 * blue/orange glow atmosphere sits behind the content.
 */
export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const { pathname } = useLocation();
  // Close the off-canvas drawer whenever the route changes (back/forward too).
  useEffect(() => setMobileNavOpen(false), [pathname, setMobileNavOpen]);
  // While the drawer is open: close on Escape and lock background scroll.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMobileNavOpen(false);
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileNavOpen, setMobileNavOpen]);
  // Open the shared chat socket app-wide so unread badges + new-message toasts
  // work on every page, not just /chat.
  useChatSocket();

  return (
    <div className="bg-background text-foreground min-h-screen">
      <AppSidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      {/* Scrim behind the off-canvas sidebar drawer (below lg only). */}
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}
      <main
        className={cn(
          "relative min-h-screen px-5 pt-6 pb-12 transition-[margin-left] duration-300 md:px-10 md:pt-7",
          collapsed ? "lg:ml-20" : "lg:ml-[248px]",
        )}
      >
        {/* Background atmosphere — clipped in its own layer so it never breaks
            the sticky tab strips that some pages render. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-56 -right-40 size-[620px] rounded-full motion-reduce:hidden"
            style={{
              background: "radial-gradient(closest-side, rgb(36 87 230 / 0.12), transparent)",
            }}
          />
          <div
            className="absolute top-28 right-14 size-44 rotate-[-30deg] rounded-full border-[1.5px] border-[color:rgb(242_140_40_/_0.35)] border-b-transparent border-l-transparent"
          />
        </div>

        <div className="relative flex flex-col gap-7">
          <AppTopBar />
          <ImpersonationBanner />
          <AnnouncementsCenter />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
